'use strict';

const EVcePositionerBase = require('../vce-positioner-base');
const CustomEvent = require('custom-event');

class EVcePositionerBlock extends EVcePositionerBase {
  init() {
    super.init();
    this.placement = 'top-end';
    this._disabled = false;
    this._borderStyle = 'solid';
    this._delay = 300;
    this._delayTimeout = false;
    this._stayOpen = false;
    this._created = false;
    this._elements = {};
    this._events.stayOpenOnEnter = this._stayOpenOnEnter.bind(this);
    this._events.closeOnLeave = this._closeOnLeave.bind(this);
    this._events.enter = this._mouseEnter.bind(this);
    this._events.leave = this._mouseLeave.bind(this);
    this.dispatchEvent(new CustomEvent('init'));
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.display = 'none';
  }

  set disabled(value) {
    this._disabled = value;
    this.close();
  }

  get disabled() {
    return this._disabled;
  }

  create(targetElement, iframe) {
    if (this._created) {
      return;
    }

    this._htmlHeight = this.ownerDocument.documentElement.style.height;
    this._bodyHeight = this.ownerDocument.body.style.height;

    this._elements.targetElement = targetElement;
    this._elements.targetDocument = targetElement.ownerDocument;
    this._elements.targetWindow = targetElement.ownerDocument.defaultView;
    this._elements.iframeElement = iframe;

    super._setupElements(targetElement, iframe);

    this._targetElement.addEventListener('mouseenter', this._events.enter);
    this._targetElement.addEventListener('mouseleave', this._events.leave);
    this._created = true;
  }

  forceClose() {
    // Polyfill needs timeout for it's disconnected callback to run
    this._targetElement.removeEventListener('mouseenter', this._events.enter);
    this._targetElement.removeEventListener('mouseleave', this._events.leave);
    this._close(popperContent => {
      setTimeout(() => {
        this.appendChild(popperContent);
        this._targetElement.addEventListener('mouseenter', this._events.enter);
        this._targetElement.addEventListener('mouseleave', this._events.leave);
      });
    });
  }

  _createCoverHelperElement() {
    const coverHelper = this.ownerDocument.createElement('div');
    const popperElementDimensions = this._popperElement.getBoundingClientRect();
    const attachElementDimensions = this._targetElement.getBoundingClientRect();
    const toolbarRight = Math.min(attachElementDimensions.width, this._elements.targetDocument.body.clientWidth);
    coverHelper.style.position = 'absolute';
    coverHelper.style.left = `${toolbarRight - popperElementDimensions.width}px`;
    if (this._flipped) {
      coverHelper.style.top = `${attachElementDimensions.height}px`;
    } else {
      coverHelper.style.top = `-${popperElementDimensions.height}px`;
    }
    coverHelper.style.width = `${popperElementDimensions.width}px`;
    coverHelper.style.height = `${popperElementDimensions.height}px`;

    return coverHelper;
  }

  _removeCoverHelperElement() {
    if (!this._coverHelperElement || !this._coverHelperElement.parentNode) {
      return;
    }

    this._coverHelperElement.parentNode.removeChild(this._coverHelperElement);
    this._coverHelperElement = null;
  }

  _createCoverElement() {
    const coverElement = this.ownerDocument.createElement('div');
    const attachElementDimensions = this._targetElement.getBoundingClientRect();
    const popperElementDimensions = this._popperElement.getBoundingClientRect();
    coverElement.setAttribute('data-coverelement', '');
    coverElement.style.position = 'absolute';
    coverElement.style.left = 0;
    if (this._flipped) {
      coverElement.style.top = `${attachElementDimensions.height}px`;
    } else {
      coverElement.style.top = `-${popperElementDimensions.height}px`;
    }
    coverElement.style.width = `${attachElementDimensions.width}px`;
    coverElement.style.height = `${popperElementDimensions.height}px`;

    return coverElement;
  }

  _removeCoverElement() {
    if (!this._coverElement || !this._coverElement.parentNode) {
      return;
    }

    this._coverElement.parentNode.removeChild(this._coverElement);
    this._coverElement = null;
  }

  _mouseEnter() {
    if (this.disabled) {
      return;
    }

    this.open(this._elements.targetElement, this._elements.iframeElement);

    requestAnimationFrame(() => {
      this._coverElement = this._createCoverElement();
      this._coverHelperElement = this._createCoverHelperElement();
      const border = this._borderer.filterElementToBorder(this._elements.targetElement);
      border.borderElement.appendChild(this._coverElement);
      border.borderElement.appendChild(this._coverHelperElement);
      this._mouseEvents('add');
      this._scrollTop = this._elements.targetWindow.pageYOffset || this._elements.targetDocument.scrollTop;
    });
  }

  _mouseLeave() {
    requestAnimationFrame(() => {
      if (this._stayOpen) {
        return;
      }

      this.close();
      this._removeCoverElement();
      this._removeCoverHelperElement();
      this._mouseEvents('remove');
    });
  }

  _mouseEvents(action) {
    if (!this._popperElement) {
      return;
    }

    const eventListenerAction = action + 'EventListener';
    [
      this._popperElement,
      this._coverElement,
      this._elements.targetElement,
      this._coverHelperElement
    ].forEach((element) => {
      if (element) {
        element[eventListenerAction]('mouseenter', this._events.stayOpenOnEnter);
        element[eventListenerAction]('mouseleave', this._events.closeOnLeave);
      }
    });
  }

  _stayOpenOnEnter(event) {
    if (this._delayTimeout) {
      clearTimeout(this._delayTimeout);
    }

    if (event.target === this._coverElement) {
      this._delayTimeout = setTimeout(this._closeOnLeave.bind(this), this._delay);
    }

    this._stayOpen = true;
  }

  _closeOnLeave() {
    if (this._delayTimeout) {
      clearTimeout(this._delayTimeout);
    }

    this._mouseLeave();
    this._stayOpen = false;
  }
}

module.exports = EVcePositionerBlock;
